import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth, UserRole } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';
import { GpsSharingProvider } from './context/GpsSharingContext';
import GpsSharingWidget from './components/GpsSharingWidget';
import OfflineBanner from './components/OfflineBanner';
import { Toaster } from './components/ui/sonner';
import Sidebar from './components/Sidebar';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import IncidentsPage from './pages/IncidentsPage';
import ResourcesPage from './pages/ResourcesPage';
import PersonsPage from './pages/PersonsPage';
import PersonnelPage from './pages/ShiftsPage';
import CommuneViewPage from './pages/CommuneViewPage';
import UsersPage from './pages/UsersPage';
import CareCentersPage from './pages/CareCentersPage';
import VehiclesPage from './pages/VehiclesPage';
import MaterialRequestsPage from './pages/MaterialRequestsPage';
import CrisisDirectoryPage from './pages/CrisisDirectoryPage';
import MissionsPage from './pages/MissionsPage';
import OrganigramPage from './pages/OrganigramPage';
import ProfilePage from './pages/ProfilePage';
import ProceduresPage from './pages/ProceduresPage';
import DocumentsPage from './pages/DocumentsPage';
import PricingPage from './pages/PricingPage';
import ChauffeurPage from './pages/ChauffeurPage';
import CartePage from './pages/CartePage';
import OrdresMissionPage from './pages/OrdresMissionPage';
import SettingsPage from './pages/SettingsPage';
import ViewerPage from './pages/ViewerPage';
import ConsolePage from './pages/ConsolePage';
import ConsoleLoginPage from './pages/ConsoleLoginPage';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user.role)) {
        // Redirect based on role
        if (user.role === UserRole.COMMUNE) {
            return <Navigate to="/commune" replace />;
        }
        if (user.role === UserRole.GYMNASE_ADMIN || user.role === UserRole.GYMNASE_CHEF_POSTE || user.role === UserRole.RENFORT_CARE) {
            return <Navigate to="/persons" replace />;
        }
        if (user.role === UserRole.MOYEN) {
            return <Navigate to="/vehicles" replace />;
        }
        if (user.role === UserRole.CHAUFFEUR_PL) {
            return <Navigate to="/chauffeur" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Layout with Sidebar
const DashboardLayout = ({ children }) => {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [syncVersion, setSyncVersion] = useState(0);

    useEffect(() => {
        const onSynced = () => setSyncVersion((v) => v + 1);
        window.addEventListener('civis-offline-synced', onSynced);
        return () => window.removeEventListener('civis-offline-synced', onSynced);
    }, []);

    return (
        <div className="dashboard-layout">
            <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
            <main className={`main-content ${sidebarCollapsed ? 'main-content-collapsed' : ''} pt-16 md:pt-0 px-3 md:px-6`}>
                <OfflineBanner />
                <React.Fragment key={syncVersion}>{children}</React.Fragment>
            </main>
            <GpsSharingWidget />
        </div>
    );
};

// Get default route based on user role
const getDefaultRoute = (role) => {
    switch (role) {
        case UserRole.COMMUNE:
            return '/commune';
        case UserRole.GYMNASE_ADMIN:
        case UserRole.GYMNASE_CHEF_POSTE:
        case UserRole.RENFORT_CARE:
            return '/persons';
        case UserRole.MOYEN:
            return '/vehicles';
        case UserRole.CHAUFFEUR_PL:
            return '/chauffeur';
        default:
            return '/dashboard';
    }
};

// Main App Routes
const AppRoutes = () => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    const defaultRoute = user ? getDefaultRoute(user.role) : '/login';

    return (
        <Routes>
            {/* Public Routes */}
            <Route 
                path="/login" 
                element={user ? <Navigate to={defaultRoute} replace /> : <LoginPage />} 
            />
            <Route path="/tarifs" element={<PricingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/viewer/:shareToken" element={<ViewerPage />} />
            <Route path="/console/login" element={<ConsoleLoginPage />} />
            <Route path="/console" element={<ConsolePage />} />

            {/* Parametres (Chef uniquement) */}
            <Route 
                path="/parametres" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF]}>
                        <DashboardLayout><SettingsPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Mode Chauffeur */}            <Route 
                path="/chauffeur" 
                element={
                    <ProtectedRoute>
                        <DashboardLayout><ChauffeurPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Dashboard */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS, UserRole.OPERATEUR_RADIO, UserRole.SOUTIEN_PSY]}>
                        <DashboardLayout><DashboardPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Incidents */}
            <Route 
                path="/incidents" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.MOYEN, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.COS]}>
                        <DashboardLayout><IncidentsPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* CARE Centers */}
            <Route 
                path="/care-centers" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.RENFORT_CARE, UserRole.SOUTIEN_PSY]}>
                        <DashboardLayout><CareCentersPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Resources */}
            <Route 
                path="/resources" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.SOUTIEN_LOG_COMMUNE]}>
                        <DashboardLayout><ResourcesPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Material Requests */}
            <Route 
                path="/material-requests" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.GYMNASE_CHEF_POSTE, UserRole.SOUTIEN_LOG_COMMUNE]}>
                        <DashboardLayout><MaterialRequestsPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Persons */}
            <Route 
                path="/persons" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.MOYEN, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.RENFORT_CARE, UserRole.SOUTIEN_PSY]}>
                        <DashboardLayout><PersonsPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Vehicles */}
            <Route 
                path="/vehicles" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.MOYEN, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.COS]}>
                        <DashboardLayout><VehiclesPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Personnel */}
            <Route 
                path="/shifts" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.GYMNASE_CHEF_POSTE, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.COS, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.SOUTIEN_PSY]}>
                        <DashboardLayout><PersonnelPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Crisis Directory */}
            <Route 
                path="/crisis-directory" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.CHAUFFEUR_PL, UserRole.SOUTIEN_PSY]}>
                        <DashboardLayout><CrisisDirectoryPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Missions */}
            <Route 
                path="/missions" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.COS, UserRole.COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.SOUTIEN_PSY]}>
                        <DashboardLayout><MissionsPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Carte de situation */}
            <Route 
                path="/carte" 
                element={
                    <ProtectedRoute>
                        <DashboardLayout><CartePage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Ordres de mission */}
            <Route 
                path="/ordres-mission" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.COMMUNE, UserRole.CHAUFFEUR_PL]}>
                        <DashboardLayout><OrdresMissionPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Organigramme */}
            <Route 
                path="/organigramme" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.COMMUNE, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.SOUTIEN_PSY]}>
                        <DashboardLayout><OrganigramPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Commune View */}
            <Route 
                path="/commune" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.COMMUNE, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION]}>
                        <DashboardLayout><CommuneViewPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Users Management */}
            <Route 
                path="/users" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF]}>
                        <DashboardLayout><UsersPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Profile */}
            <Route 
                path="/profile" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.COMMUNE, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.CHAUFFEUR_PL, UserRole.SOUTIEN_PSY]}>
                        <DashboardLayout><ProfilePage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Procedures communales */}
            <Route 
                path="/procedures" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.COMMUNE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS]}>
                        <DashboardLayout><ProceduresPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Documents communaux */}
            <Route 
                path="/documents" 
                element={
                    <ProtectedRoute allowedRoles={[UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.COMMUNE, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION]}>
                        <DashboardLayout><DocumentsPage /></DashboardLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to={defaultRoute} replace />} />
            <Route path="*" element={<Navigate to={defaultRoute} replace />} />
        </Routes>
    );
};

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
            <AuthProvider>
                <GpsSharingProvider>
                <NotificationProvider>
                    <div className="app-container">
                        <AppRoutes />
                        <Toaster 
                            position="top-right"
                            richColors
                        />
                    </div>
                </NotificationProvider>
                </GpsSharingProvider>
            </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
